import { defineStore } from 'pinia'
import { ref } from 'vue'
import type * as THREE from 'three'
import type { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import type { CoordinateTuple } from '@webgamekit/threejs'
import { usePerfMetricsStore } from './perfMetrics'
import { useElementPropertiesStore, type ElementPropertiesConfig } from './elementProperties'
import { registerCameraProperties } from '@/utils/cameraProperties'
import { numberDuplicateNames } from '@/utils/elementNames'
import { LIGHT_ELEMENT_NAMES } from '@/utils/sceneLights'
import { useSceneViewStore } from '@/stores/sceneView'

const GENERIC_THREE_TYPES = new Set([
  'Mesh',
  'Object3D',
  'Group',
  'Line',
  'Points',
  'Sprite',
  'InstancedMesh',
  'SkinnedMesh',
  'LineSegments',
  'LineLoop'
])

const warnIfGenericName = (name: string) => {
  if (import.meta.env.DEV && (!name || GENERIC_THREE_TYPES.has(name))) {
    console.warn(
      `[ElementsPanel] Element registered with generic name "${name}". Provide a descriptive scene-role name instead.`
    )
  }
}

export interface SceneElement {
  name: string
  label?: string
  type: string
  hidden?: boolean
  groupId?: string
  spawnId?: string
  /** uuid of the Three.js object, so a numbered duplicate still resolves to its own object. */
  objectId?: string
}

export interface SpawnEntry {
  id: string
  label: string
  hidden?: boolean
}

export interface DebugSceneHandlers {
  onToggleVisibility: (name: string) => void
  onRemove: (name: string) => void
}

export interface InstancedGroupHandlers {
  onAdd: (position: CoordinateTuple) => void
  onDelete: (index: number) => void
  onUpdate: (index: number, position: CoordinateTuple) => void
  onToggleVisibility: (hidden: boolean) => void
  onRemove: () => void
}

export interface InstancedGroup {
  id: string
  label: string
  positions: CoordinateTuple[]
  hidden?: boolean
  handlers: InstancedGroupHandlers
}

export interface SpawnGroupHandlers {
  onToggleVisibility: (hidden: boolean) => void
  onRemove: () => void
}

export interface SpawnGroup {
  id: string
  label: string
  count: number
  hidden?: boolean
  handlers: SpawnGroupHandlers
}

export interface PathConfig {
  speed: number
  obstacleImpulse: number
  /** Smooth the route through its waypoints, rather than joining them with straight segments. */
  curved: boolean
  easing: string
  easingIntensity: number
  playing: boolean
  loop: boolean
  pingPong: boolean
  showPath: boolean
  showNodes: boolean
}

export interface PathHandlers {
  onAddWaypoint: (position: CoordinateTuple) => void
  onRemoveWaypoint: (index: number) => void
  onUpdateWaypoint: (index: number, position: CoordinateTuple) => void
  onReset: () => void
  onToggleVisibility: (hidden: boolean) => void
  onRemove: () => void
  onConfigChange: (key: keyof PathConfig, value: unknown) => void
}

export interface PathEntry {
  id: string
  elementName: string
  label: string
  waypoints: CoordinateTuple[]
  config: PathConfig
  hidden?: boolean
  /** Stepped paths classify each segment as a walk / forward-jump / in-place jump. */
  stepped?: boolean
  handlers: PathHandlers
}

export interface PathRegistrationContext {
  onEnablePath: (elementName: string) => void
}

export const useDebugSceneStore = defineStore('debugScene', () => {
  const sceneElements = ref<SceneElement[]>([])
  const sceneGroups = ref<Record<string, string>>({})
  const spawns = ref<SpawnEntry[]>([])
  const instancedGroups = ref<InstancedGroup[]>([])
  const spawnGroups = ref<SpawnGroup[]>([])
  const paths = ref<PathEntry[]>([])
  /** Waypoint currently being interacted with (e.g. dragged), highlighted in the panel. */
  const activeWaypoint = ref<{ pathId: string; index: number } | null>(null)
  const setActiveWaypoint = (pathId: string, index: number | null) => {
    activeWaypoint.value = index === null ? null : { pathId, index }
  }
  const handlers = ref<DebugSceneHandlers | null>(null)
  const spawnHandlers = ref<Record<string, () => void>>({})
  const pathRegistrationContext = ref<PathRegistrationContext | null>(null)

  const setSceneElements = (
    elements: SceneElement[],
    newHandlers: DebugSceneHandlers,
    groups?: Record<string, string>
  ) => {
    sceneElements.value = elements
    handlers.value = newHandlers
    sceneGroups.value = groups ?? {}
  }

  interface RegisterSceneOptions {
    renderer?: THREE.WebGLRenderer
    orbit?: OrbitControls | null
    setCamera?: (newCamera: THREE.Camera) => OrbitControls | null
  }

  type ObjectEntry = { name?: string; type: string; visible?: boolean; parent?: unknown }

  const resolveRawNames = (objects: ObjectEntry[]): string[] =>
    objects.map((element) => {
      const rawName = element.name || element.type
      warnIfGenericName(rawName)
      return rawName
    })

  const resolveElementNames = (objects: ObjectEntry[]): SceneElement[] =>
    numberDuplicateNames(resolveRawNames(objects)).map((name, index) => ({
      name,
      type: objects[index].type,
      hidden: false
    }))

  /**
   * Put every light behind the one Lights element, as a scene the view store set up already
   * does: a row per lamp is six rows saying what one panel says better. The scene goes to
   * that store too, so the element has real lights to drive.
   * @param objects - The scene children being registered
   * @returns The Lights row, if any, and everything that still needs its own
   */
  const groupLights = (objects: ObjectEntry[]) => {
    const isLight = (object: ObjectEntry) => LIGHT_ELEMENT_NAMES.has(object.name ?? '')
    const lit = objects.filter(isLight)
    const litScene = lit[0]?.parent as THREE.Scene | undefined
    if (litScene) useSceneViewStore().adoptSceneLights(litScene)
    return {
      lightsElement: lit.length
        ? ([{ name: 'Lights', type: 'Lights', hidden: false }] as SceneElement[])
        : [],
      rest: objects.filter((object) => !isLight(object))
    }
  }

  const registerSceneElements = (
    camera: THREE.Camera,
    objects: ObjectEntry[],
    elementHandlers?: Partial<DebugSceneHandlers>,
    options?: RegisterSceneOptions
  ) => {
    const { renderer, orbit, setCamera } = options ?? {}
    const defaultToggleVisibility = (name: string) => {
      const sceneObject = objects.find((o) => o.name === name) as { visible?: boolean } | undefined
      if (sceneObject && typeof sceneObject.visible === 'boolean') {
        Reflect.set(sceneObject, 'visible', !sceneObject.visible)
      }
    }
    const { lightsElement, rest } = groupLights(objects)

    setSceneElements(
      [
        { name: 'Camera', type: camera.type, hidden: false },
        ...lightsElement,
        ...resolveElementNames(rest)
      ],
      {
        onToggleVisibility: elementHandlers?.onToggleVisibility ?? defaultToggleVisibility,
        onRemove: elementHandlers?.onRemove ?? (() => {})
      }
    )
    if ('fov' in camera) {
      registerCameraProperties({
        camera,
        orbit,
        renderer,
        // Wrapped so the row's type follows the swap: it was read off the camera when the list
        // was built, and an orthographic camera would otherwise still call itself perspective.
        setCamera: setCamera
          ? (newCamera: THREE.Camera) => {
              const nextOrbit = setCamera(newCamera)
              updateSceneElementType('Camera', newCamera.type)
              return nextOrbit
            }
          : undefined
      })
    }
    if (renderer) usePerfMetricsStore().setRenderer(renderer)
  }

  const registerSpawn = (id: string, label: string, onToggleVisibility?: () => void) => {
    spawns.value = [...spawns.value.filter((s) => s.id !== id), { id, label }]
    if (onToggleVisibility) {
      spawnHandlers.value = { ...spawnHandlers.value, [id]: onToggleVisibility }
    }
  }

  const toggleSpawnVisibility = (id: string) => {
    spawns.value = spawns.value.map((s) => (s.id === id ? { ...s, hidden: !s.hidden } : s))
    spawnHandlers.value[id]?.()
  }

  const unregisterSpawn = (id: string) => {
    spawns.value = spawns.value.filter((s) => s.id !== id)
    const { [id]: _removed, ...rest } = spawnHandlers.value
    spawnHandlers.value = rest
  }

  const addSceneElement = (element: SceneElement, properties: ElementPropertiesConfig) => {
    warnIfGenericName(element.name)
    if (sceneElements.value.some((e) => e.name === element.name)) return
    sceneElements.value = [...sceneElements.value, element]
    useElementPropertiesStore().registerElementProperties(element.name, properties)
  }

  const removeSceneElement = (name: string) => {
    sceneElements.value = sceneElements.value.filter((e) => e.name !== name)
    useElementPropertiesStore().unregisterElementProperties(name)
  }

  /**
   * Retypes one element in place, for a camera whose projection has been swapped.
   *
   * Narrow on purpose: rebuilding the whole list from the scene would be the obvious fix, and
   * it drops every row registered on top of it — a follow rig, a path — because those are not
   * scene children.
   * @param name The element to retype
   * @param type Its new type, shown in the panel and used to route its controls
   */
  const updateSceneElementType = (name: string, type: string) => {
    sceneElements.value = sceneElements.value.map((element) =>
      element.name === name ? { ...element, type } : element
    )
  }

  const clearSceneElements = () => {
    sceneElements.value = []
    sceneGroups.value = {}
    spawns.value = []
    instancedGroups.value = []
    spawnGroups.value = []
    paths.value = []
    handlers.value = null
    usePerfMetricsStore().clearRenderer()
  }

  const handleToggleVisibility = (name: string) => {
    sceneElements.value = sceneElements.value.map((e) =>
      e.name === name ? { ...e, hidden: !e.hidden } : e
    )
    handlers.value?.onToggleVisibility(name)
  }

  const handleRemove = (name: string) => {
    handlers.value?.onRemove(name)
    removeSceneElement(name)
  }

  const addInstancedGroup = (group: InstancedGroup) => {
    const existing = instancedGroups.value.find((g) => g.id === group.id)
    const merged = { ...group, hidden: group.hidden ?? existing?.hidden ?? false }
    instancedGroups.value = [...instancedGroups.value.filter((g) => g.id !== group.id), merged]
  }

  const removeInstancedGroup = (id: string) => {
    const group = instancedGroups.value.find((g) => g.id === id)
    group?.handlers.onRemove()
    instancedGroups.value = instancedGroups.value.filter((g) => g.id !== id)
  }

  const toggleInstancedGroupVisibility = (id: string) => {
    instancedGroups.value = instancedGroups.value.map((g) => {
      if (g.id !== id) return g
      const hidden = !g.hidden
      g.handlers.onToggleVisibility(hidden)
      return { ...g, hidden }
    })
  }

  const updateInstancedGroupPosition = (id: string, index: number, position: CoordinateTuple) => {
    instancedGroups.value = instancedGroups.value.map((g) =>
      g.id !== id
        ? g
        : {
            ...g,
            positions: g.positions.map((p, i) => (i === index ? position : p))
          }
    )
  }

  const addSpawnGroup = (group: SpawnGroup) => {
    const existing = spawnGroups.value.find((g) => g.id === group.id)
    const merged = { ...group, hidden: group.hidden ?? existing?.hidden ?? false }
    spawnGroups.value = [...spawnGroups.value.filter((g) => g.id !== group.id), merged]
  }

  const removeSpawnGroup = (id: string) => {
    const group = spawnGroups.value.find((g) => g.id === id)
    group?.handlers.onRemove()
    spawnGroups.value = spawnGroups.value.filter((g) => g.id !== id)
    useElementPropertiesStore().unregisterElementProperties(id)
  }

  const toggleSpawnGroupVisibility = (id: string) => {
    spawnGroups.value = spawnGroups.value.map((g) => {
      if (g.id !== id) return g
      const hidden = !g.hidden
      g.handlers.onToggleVisibility(hidden)
      return { ...g, hidden }
    })
  }

  const setSpawnGroupCount = (id: string, count: number) => {
    spawnGroups.value = spawnGroups.value.map((g) => (g.id === id ? { ...g, count } : g))
  }

  const registerPathContext = (context: PathRegistrationContext) => {
    pathRegistrationContext.value = context
  }

  const unregisterPathContext = () => {
    pathRegistrationContext.value = null
  }

  const enablePathForElement = (elementName: string) => {
    pathRegistrationContext.value?.onEnablePath(elementName)
  }

  const addPath = (entry: PathEntry) => {
    const existing = paths.value.find((p) => p.id === entry.id)
    const merged = { ...entry, hidden: entry.hidden ?? existing?.hidden ?? false }
    paths.value = [...paths.value.filter((p) => p.id !== entry.id), merged]
  }

  const removePath = (id: string) => {
    const entry = paths.value.find((p) => p.id === id)
    entry?.handlers.onRemove()
    paths.value = paths.value.filter((p) => p.id !== id)
    useElementPropertiesStore().unregisterElementProperties(id)
  }

  const togglePathVisibility = (id: string) => {
    const entry = paths.value.find((p) => p.id === id)
    if (!entry) return
    const hidden = !entry.hidden
    paths.value = paths.value.map((p) => (p.id === id ? { ...p, hidden } : p))
    entry.handlers.onToggleVisibility(hidden)
  }

  const addPathWaypoint = (id: string, position: CoordinateTuple) => {
    const entry = paths.value.find((p) => p.id === id)
    if (!entry) return
    paths.value = paths.value.map((p) =>
      p.id === id ? { ...p, waypoints: [...p.waypoints, position] } : p
    )
    entry.handlers.onAddWaypoint(position)
  }

  const removePathWaypoint = (id: string, index: number) => {
    const entry = paths.value.find((p) => p.id === id)
    if (!entry) return
    paths.value = paths.value.map((p) =>
      p.id === id ? { ...p, waypoints: p.waypoints.filter((_, i) => i !== index) } : p
    )
    entry.handlers.onRemoveWaypoint(index)
  }

  const updatePathWaypoint = (id: string, index: number, position: CoordinateTuple) => {
    const entry = paths.value.find((p) => p.id === id)
    if (!entry) return
    paths.value = paths.value.map((p) =>
      p.id === id ? { ...p, waypoints: p.waypoints.map((w, i) => (i === index ? position : w)) } : p
    )
    entry.handlers.onUpdateWaypoint(index, position)
  }

  const updatePathConfig = (id: string, key: keyof PathConfig, value: unknown) => {
    const entry = paths.value.find((p) => p.id === id)
    if (!entry) return
    paths.value = paths.value.map((p) =>
      p.id === id ? { ...p, config: { ...p.config, [key]: value } } : p
    )
    entry.handlers.onConfigChange(key, value)
  }

  const moveElementAfter = (name: string, afterName: string) => {
    const index = sceneElements.value.findIndex((e) => e.name === name)
    const afterIndex = sceneElements.value.findIndex((e) => e.name === afterName)
    if (index === -1 || afterIndex === -1 || index === afterIndex + 1) return
    const element = sceneElements.value[index]
    const without = sceneElements.value.filter((_, i) => i !== index)
    const insertAt = without.findIndex((e) => e.name === afterName) + 1
    sceneElements.value = [...without.slice(0, insertAt), element, ...without.slice(insertAt)]
  }

  return {
    sceneElements,
    sceneGroups,
    spawns,
    instancedGroups,
    spawnGroups,
    paths,
    activeWaypoint,
    setActiveWaypoint,
    setSceneElements,
    addSceneElement,
    removeSceneElement,
    updateSceneElementType,
    registerSceneElements,
    addInstancedGroup,
    removeInstancedGroup,
    toggleInstancedGroupVisibility,
    updateInstancedGroupPosition,
    addSpawnGroup,
    removeSpawnGroup,
    toggleSpawnGroupVisibility,
    setSpawnGroupCount,
    registerPathContext,
    unregisterPathContext,
    enablePathForElement,
    addPath,
    removePath,
    togglePathVisibility,
    addPathWaypoint,
    removePathWaypoint,
    updatePathWaypoint,
    updatePathConfig,
    registerSpawn,
    unregisterSpawn,
    toggleSpawnVisibility,
    clearSceneElements,
    handleToggleVisibility,
    handleRemove,
    moveElementAfter
  }
})
